<template>
  <div>
    <a-drawer
      width="min(640px, 100vw)"
      placement="right"
      wrap-class-name="support-chat-drawer"
      :closable="false"
      :visible="visible"
      @close="closeChat"
    >
      <header class="support-header">
        <span class="support-avatar"><img :src="systemLogo" alt="" /></span>
        <div class="support-identity">
          <strong>ePeople Support</strong>
          <span><i aria-hidden="true" />Online</span>
        </div>
        <button type="button" class="support-close" aria-label="Close support chat" @click="closeChat">
          <a-icon icon="carbon:close" width="22" height="22" />
        </button>
      </header>

      <div
        ref="messagesContainer"
        class="messages"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Support conversation"
        @scroll="handleMessagesScroll"
      >
        <support-chat-message
          v-for="message in displayMessages"
          :key="message.id"
          :message="message"
          :date="message.created_at ? formatDate(message.created_at) : ''"
          :conversation-id="conversationId"
          :system-logo="systemLogo"
          :retrying="retryingMessageId === message.id"
          @retry="retryMessage"
          @replace-image="replaceFailedImage"
          @remove-image="removeFailedImage"
        >
          <support-conversation-action
            v-if="isActionTarget(message)"
            :key="currentActionKey"
            :action="currentAction"
            :disabled="
              loadingSend || loadingFeedback || loadingImageChange || confirmingImagePolicy || visibleFeedbackForm
            "
            :has-draft="hasDraft"
            @open-support-form="openFeedbackForm"
            @conversation-changed="scrollToBottom"
            @dismissed="focusMessageInput"
            @loading-change="loadingAction = $event"
          />
        </support-chat-message>

        <div v-if="loadingSend" class="support-typing" role="status">
          <img :src="systemLogo" alt="" />
          <span>ePeople Support is responding…</span>
        </div>
        <button v-if="hasUnreadReply" type="button" class="new-reply-button" @click="scrollToLatestReply">
          New reply — jump to latest
        </button>
      </div>

      <div class="footer">
        <form
          v-if="visibleFeedbackForm"
          class="support-form"
          data-testid="support-request-form"
          @submit.prevent="sendFeedback"
        >
          <h4 class="m-0">{{ feedbackFormTitle }}</h4>
          <label id="support-request-helper" for="support-request-text">{{ feedbackHelperText }}</label>
          <div v-if="isIssueFeedback && feedbackAttachments.length" class="feedback-attachments">
            <strong>Include screenshots</strong>
            <p>Select only the screenshots you want to share with Support.</p>
            <a-checkbox-group v-model="selectedFeedbackAttachmentIds">
              <div v-for="attachment in feedbackAttachments" :key="attachment.file_id" class="feedback-attachment">
                <a-checkbox :value="attachment.file_id">Screenshot {{ attachment.file_id }}</a-checkbox>
                <support-message-attachment :attachment="attachment" :conversation-id="conversationId" />
              </div>
            </a-checkbox-group>
            <a-checkbox
              v-if="selectedFeedbackAttachmentIds.length"
              v-model="feedbackAttachmentConsent"
              class="feedback-attachment-consent"
            >
              I agree to include the selected screenshots with this request.
            </a-checkbox>
            <p v-if="feedbackAttachmentValidation" class="support-validation" role="alert">
              {{ feedbackAttachmentValidation }}
            </p>
          </div>
          <a-textarea
            id="support-request-text"
            ref="supportInput"
            v-model="supportText"
            :disabled="loadingFeedback"
            :aria-invalid="Boolean(supportValidation)"
            :aria-describedby="
              supportValidation ? 'support-request-helper support-request-validation' : 'support-request-helper'
            "
            :style="{ resize: 'none' }"
            :auto-size="{ minRows: 3, maxRows: 5 }"
            :placeholder="feedbackPlaceholder"
            data-testid="support-request-textarea"
            spellcheck
          />
          <p
            v-if="supportValidation"
            id="support-request-validation"
            class="support-validation"
            role="alert"
            data-testid="support-request-validation"
          >
            {{ supportValidation }}
          </p>
          <a-alert
            v-if="feedbackStatus === 'error'"
            message="We could not send this right now. Please try again."
            type="error"
            show-icon
            role="alert"
            data-testid="support-request-error"
          />
          <div class="flex justify-content-between gap-2">
            <a-button
              :disabled="loadingFeedback"
              data-testid="support-request-cancel-button"
              @click="closeFeedbackForm"
            >
              Cancel
            </a-button>
            <a-button
              type="primary"
              html-type="submit"
              :loading="loadingFeedback"
              data-testid="support-request-send-button"
            >
              {{ feedbackSubmitLabel }}
            </a-button>
          </div>
        </form>
        <div
          v-else
          class="support-composer"
          :class="{ dragging: draggingImage }"
          @dragenter="handleImageDragEnter"
          @dragover="handleImageDragOver"
          @dragleave="handleImageDragLeave"
          @drop="handleImageDrop"
        >
          <support-image-attachment
            ref="supportImageAttachment"
            :file="imageFile"
            :disabled="loadingSend || loadingAction || loadingImageChange || confirmingImagePolicy || !canSend"
            :active="visible && !visibleFeedbackForm && canSend"
            @select="selectImage"
            @remove="removeImage"
          />
          <a-checkbox
            v-if="canReusePreviousAttachment"
            v-model="reusePreviousAttachment"
            :disabled="loadingSend || loadingAction || confirmingImagePolicy"
            class="reuse-previous-attachment"
          >
            Include previous screenshot
          </a-checkbox>
          <div
            v-if="confirmingImagePolicy"
            class="image-policy-confirmation"
            role="group"
            aria-labelledby="image-policy-title"
          >
            <strong id="image-policy-title">Before You Send</strong>
            <p>
              Make sure the image does not show passwords, security codes, Social Security numbers, bank details, or
              patient information.
            </p>
            <div class="confirmation-actions">
              <a-button @click="cancelImagePolicy">Cancel</a-button>
              <a-button type="primary" @click="acceptImagePolicy">Send Image</a-button>
            </div>
          </div>
          <div class="composer-box">
            <label class="sr-only" for="support-chat-input">Message ePeople Support</label>
            <a-textarea
              id="support-chat-input"
              ref="messageInput"
              v-model="text"
              class="chat-input"
              :disabled="loadingAction || !canSend"
              :style="{ resize: 'none' }"
              :auto-size="{ minRows: 1, maxRows: 2 }"
              spellcheck
              placeholder="Type what you need help with…"
              @pressEnter="sendTextMessage"
            />
            <button
              type="button"
              :disabled="
                loadingSend || loadingAction || loadingImageChange || confirmingImagePolicy || !canSend || !hasDraft
              "
              class="chat-send"
              aria-label="Send message"
              data-testid="support-chat-send-button"
              @click="sendMessage"
            >
              <a-icon width="24" height="24" icon="carbon:send-alt" />
            </button>
          </div>
          <p class="composer-hint">Enter to send · Shift + Enter for a new line</p>
        </div>
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { Alert, Button, Checkbox, Drawer, Input, notification } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment-timezone'
import { getSupportIdentity } from '@/services/support-diagnostics'
import SupportChatMessage from './support-chat-message.vue'
import SupportConversationAction from './support-conversation-action.vue'
import SupportImageAttachment from './support-image-attachment.vue'
import SupportMessageAttachment from './support-message-attachment.vue'
import {
  SUPPORT_ACTION_LABELS,
  SUPPORT_ACTION_REASONS,
  getSupportActionKey,
  isKnownSupportAction
} from '@/store/modules/agent/constants'

const { mapActions, mapState } = createNamespacedHelpers('agent')

export default {
  components: {
    'a-drawer': Drawer,
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-checkbox-group': Checkbox.Group,
    'a-textarea': Input.TextArea,
    'a-alert': Alert,
    SupportChatMessage,
    SupportConversationAction,
    SupportImageAttachment,
    SupportMessageAttachment
  },
  data() {
    return {
      visibleFeedbackForm: false,
      supportValidation: '',
      feedbackStatus: '',
      feedbackReason: '',
      selectedFeedbackAttachmentIds: [],
      feedbackAttachmentConsent: false,
      feedbackAttachmentValidation: '',
      failedMessages: [],
      systemMessages: [],
      localMessageSequence: 0,
      retryingMessageId: null,

      loading: false,
      loadingSend: false,
      loadingFeedback: false,
      loadingAction: false,
      loadingImageChange: false,
      confirmingImagePolicy: false,

      text: '',
      supportText: '',
      imageFile: null,
      reusePreviousAttachment: false,
      draggingImage: false,
      imageDragDepth: 0,
      isNearBottom: true,
      hasUnreadReply: false,

      systemLogo: require('@/assets/logo.png')
    }
  },
  computed: {
    ...mapState({
      conversation: state => state.conversation,
      visible: state => state.visible,
      conversationVersion: state => state.conversationVersion,
      imagePolicyConfirmed: state => state.imagePolicyConfirmed,
      eventDescription: state => state.eventDescription
    }),

    messages() {
      return this.conversation?.messages ?? []
    },

    canSend() {
      return this.conversation?.can_send === true
    },

    hasDraft() {
      return Boolean(this.text.trim() || this.imageFile)
    },

    conversationId() {
      return this.conversation?.id ?? null
    },

    lastPersistedUserMessage() {
      return [...this.messages].reverse().find(message => message.role === 'user' && !message.temporary) ?? null
    },

    canReusePreviousAttachment() {
      return !this.imageFile && Boolean(this.lastPersistedUserMessage?.attachment?.file_id)
    },

    feedbackAttachments() {
      return [
        ...new Map(
          this.messages
            .filter(message => message.attachment?.file_id)
            .map(message => [message.attachment.file_id, message.attachment])
        ).values()
      ]
    },

    currentAction() {
      return this.conversation?.actions?.find(isKnownSupportAction) ?? null
    },

    currentActionKey() {
      return `${getSupportActionKey(this.currentAction)}:${this.currentAction?.data?.contact_id ?? ''}`
    },

    actionTargetId() {
      return [...this.messages].reverse().find(message => message.role === 'assistant')?.id ?? null
    },

    feedbackFormTitle() {
      return this.feedbackReason === SUPPORT_ACTION_REASONS.UNRESOLVED_TECHNICAL_ISSUE
        ? SUPPORT_ACTION_LABELS.SEND_ISSUE_TO_SUPPORT
        : SUPPORT_ACTION_LABELS.CONTACT_SUPPORT
    },

    feedbackSubmitLabel() {
      return this.feedbackReason === SUPPORT_ACTION_REASONS.UNRESOLVED_TECHNICAL_ISSUE
        ? SUPPORT_ACTION_LABELS.SEND_ISSUE_TO_SUPPORT
        : 'Send to Support'
    },

    isIssueFeedback() {
      return this.feedbackReason === SUPPORT_ACTION_REASONS.UNRESOLVED_TECHNICAL_ISSUE
    },

    feedbackHelperText() {
      return this.isIssueFeedback
        ? 'Your conversation and available error details will be included. You can add anything we missed.'
        : 'Please write what happened.'
    },

    feedbackPlaceholder() {
      return this.isIssueFeedback ? 'Add optional details…' : 'What is not working?'
    },

    supportIdentity() {
      return getSupportIdentity(this.$store.state.applicationState)
    },

    displayMessages() {
      const failedMessages = this.failedMessages.filter(message => message.id !== this.retryingMessageId)
      const messages = [...this.messages, ...failedMessages, ...this.systemMessages].sort(
        (a, b) => a.created_at - b.created_at
      )

      if (!this.eventDescription) return messages
      return [
        {
          id: 'support-error-context',
          role: 'system',
          type: 'error',
          title: 'Help with this error',
          content: this.eventDescription,
          detail: 'Error details were added automatically.'
        },
        ...messages
      ]
    }
  },
  watch: {
    supportText(value) {
      if (value.trim()) this.supportValidation = ''
    },
    selectedFeedbackAttachmentIds() {
      this.feedbackAttachmentConsent = false
      this.feedbackAttachmentValidation = ''
    },
    supportIdentity() {
      this.text = ''
      this.supportText = ''
      this.imageFile = null
      this.reusePreviousAttachment = false
      this.supportValidation = ''
      this.feedbackStatus = ''
      this.feedbackReason = ''
      this.resetFeedbackAttachments()
      this.failedMessages = []
      this.systemMessages = []
      this.retryingMessageId = null
      this.visibleFeedbackForm = false
    },
    conversationId(value, previousValue) {
      if (value !== previousValue) this.reusePreviousAttachment = false
      if (previousValue && value && value !== previousValue) {
        this.text = ''
        this.imageFile = null
        this.failedMessages = []
        this.systemMessages = []
        this.retryingMessageId = null
        this.resetFeedbackAttachments()
        this.hasUnreadReply = false
      }
    },
    conversationVersion: {
      handler() {
        this.loading = false
        this.loadingSend = false
        this.loadingFeedback = false
        this.loadingAction = false
        this.loadingImageChange = false
        this.confirmingImagePolicy = false
        if (this.visible) this.init()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions([
      'getMessages',
      'postMessage',
      'postFeedback',
      'uploadSupportImage',
      'deleteSupportAttachment',
      'getSupportAttachmentUrl'
    ]),

    closeChat() {
      this.$store.commit('agent/CLOSE_SUPPORT')
      this.$nextTick(() => document.querySelector('[data-testid="layout-support-entry"]')?.focus())
    },

    showNotification(type, message, description, duration = 10) {
      notification[type]({
        message,
        description,
        duration,
        onClose: close
      })
    },

    formatDate(date) {
      const now = moment()
      const target = moment.utc(date * 1000).tz(this.$store.state.applicationState.timeZone)

      if (target.isSame(now, 'day')) {
        return target.format('hh:mm A')
      } else {
        return target.format('MM/DD/YYYY hh:mm A')
      }
    },

    isActionTarget(message) {
      return Boolean(this.currentAction && message.id === this.actionTargetId)
    },

    openFeedbackForm(reason) {
      this.supportValidation = ''
      this.feedbackStatus = ''
      this.feedbackReason = reason
      this.resetFeedbackAttachments()
      this.visibleFeedbackForm = true
      this.$nextTick(() => this.$refs.supportInput?.focus())
    },

    closeFeedbackForm() {
      if (this.loadingFeedback) return

      this.supportText = ''
      this.supportValidation = ''
      this.feedbackStatus = ''
      this.feedbackReason = ''
      this.resetFeedbackAttachments()
      this.visibleFeedbackForm = false
    },

    scrollToBottom(force = false) {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container && (force || this.isNearBottom)) {
          container.scrollTop = container.scrollHeight
          this.hasUnreadReply = false
        }
      })
    },

    scrollToLatestReply() {
      this.scrollToBottom(true)
    },

    handleReplySettled() {
      if (this.isNearBottom) this.scrollToBottom()
      else this.hasUnreadReply = true
    },

    handleMessagesScroll(event) {
      const container = event.target
      this.isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 32
      if (this.isNearBottom) this.hasUnreadReply = false
    },

    focusMessageInput() {
      this.$nextTick(() => this.$refs.messageInput?.focus())
    },

    resetFeedbackAttachments() {
      this.selectedFeedbackAttachmentIds = []
      this.feedbackAttachmentConsent = false
      this.feedbackAttachmentValidation = ''
    },

    async sendTextMessage(e) {
      if (!e.shiftKey && !e.isComposing) {
        e.preventDefault()
        this.sendMessage()
      }
    },

    hasDraggedFile(event) {
      return (
        Array.from(event.dataTransfer?.types ?? []).includes('Files') ||
        Array.from(event.dataTransfer?.items ?? []).some(item => item.kind === 'file')
      )
    },

    handleImageDragEnter(event) {
      if (!this.hasDraggedFile(event)) return

      event.preventDefault()
      if (this.loadingSend || this.loadingAction || this.loadingImageChange || !this.canSend) return

      this.imageDragDepth += 1
      this.draggingImage = true
    },

    handleImageDragOver(event) {
      if (!this.hasDraggedFile(event)) return

      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },

    handleImageDragLeave(event) {
      if (!this.draggingImage) return

      event.preventDefault()
      this.imageDragDepth = Math.max(0, this.imageDragDepth - 1)
      this.draggingImage = this.imageDragDepth > 0
    },

    handleImageDrop(event) {
      const file = Array.from(event.dataTransfer?.files ?? [])[0]
      if (this.hasDraggedFile(event) || file) event.preventDefault()
      this.imageDragDepth = 0
      this.draggingImage = false
      if (!file) return

      if (!this.visible || this.visibleFeedbackForm || this.loadingSend || this.loadingAction || !this.canSend) return

      this.$refs.supportImageAttachment?.selectFile(file)
    },

    selectImage(file) {
      if (this.loadingImageChange) return
      this.imageFile = file
      this.reusePreviousAttachment = false
      this.confirmingImagePolicy = false
    },

    removeImage() {
      if (this.loadingImageChange) return
      this.imageFile = null
      this.confirmingImagePolicy = false
    },

    cancelImagePolicy() {
      this.confirmingImagePolicy = false
      this.$nextTick(() => this.$refs.messageInput?.focus())
    },

    acceptImagePolicy() {
      this.$store.commit('agent/CONFIRM_IMAGE_POLICY')
      this.confirmingImagePolicy = false
      this.sendMessage()
    },

    requestImagePolicy() {
      this.confirmingImagePolicy = true
      this.$nextTick(() => this.scrollToBottom())
    },

    async sendMessage() {
      if (
        this.loadingSend ||
        this.loadingAction ||
        this.loadingImageChange ||
        this.confirmingImagePolicy ||
        !this.canSend ||
        !this.hasDraft
      )
        return

      const version = this.conversationVersion
      const conversationId = this.conversationId
      const content = this.text
      const imageFile = this.imageFile
      const reusePreviousAttachment = this.reusePreviousAttachment && this.canReusePreviousAttachment

      if (imageFile && !this.imagePolicyConfirmed) {
        this.requestImagePolicy()
        return
      }

      this.text = ''
      if (this.imageFile === imageFile) this.imageFile = null
      this.reusePreviousAttachment = false
      await this.submitMessage({ content, imageFile, reusePreviousAttachment, version, conversationId })
    },

    async retryMessage(message) {
      if (this.loadingSend || this.loadingAction || this.loadingImageChange || !this.canSend) return

      if (message.imageFile && !this.imagePolicyConfirmed) {
        this.imageFile = message.imageFile
        this.text = message.content
        this.failedMessages = this.failedMessages.filter(item => item.id !== message.id)
        this.requestImagePolicy()
        return
      }

      this.retryingMessageId = message.id
      await this.submitMessage({
        content: message.content,
        imageFile: message.imageFile,
        attachment: message.attachment,
        reusePreviousAttachment: message.reusePreviousAttachment,
        failedId: message.id,
        version: this.conversationVersion,
        conversationId: this.conversationId
      })
      this.retryingMessageId = null
    },

    async replaceFailedImage({ message, file }) {
      const index = this.failedMessages.findIndex(item => item.id === message.id)
      if (index === -1) return
      await this.deleteFailedAttachment(message)
      this.$set(this.failedMessages, index, { ...message, imageFile: file, attachment: null, errorMessage: '' })
    },

    async removeFailedImage(message) {
      const index = this.failedMessages.findIndex(item => item.id === message.id)
      if (index === -1) return
      await this.deleteFailedAttachment(message)
      if (!message.content.trim()) this.failedMessages.splice(index, 1)
      else this.$set(this.failedMessages, index, { ...message, imageFile: null, attachment: null, errorMessage: '' })
    },

    async deleteFailedAttachment(message) {
      if (!message.attachment?.file_id) return
      try {
        await this.deleteSupportAttachment({ fileId: message.attachment.file_id, conversationId: this.conversationId })
      } catch (error) {
        this.showNotification('error', 'Error', error?.message || 'The image could not be removed.')
      }
    },

    async submitMessage({
      content,
      imageFile,
      attachment = null,
      reusePreviousAttachment = false,
      failedId = null,
      version,
      conversationId
    }) {
      this.loadingSend = true
      let nextAttachment = attachment
      try {
        if (imageFile && !nextAttachment) {
          nextAttachment = await this.uploadSupportImage({ image: imageFile })

          if (conversationId !== this.conversationId) {
            await this.deleteSupportAttachment({ fileId: nextAttachment.file_id, conversationId }).catch(() => {})
            return
          }
        }

        if (version !== this.conversationVersion || conversationId !== this.conversationId) return

        const payload = {
          content,
          attachment: nextAttachment,
          imageAnalysisConsent: Boolean(nextAttachment),
          reusePreviousAttachment
        }
        const request = this.postMessage(payload)
        this.scrollToBottom(true)
        const sent = await request
        if (!sent || version !== this.conversationVersion) return

        if (failedId) this.failedMessages = this.failedMessages.filter(message => message.id !== failedId)
        this.handleReplySettled()
      } catch (error) {
        if (version !== this.conversationVersion || conversationId !== this.conversationId) return

        const failedMessage = {
          id: failedId || `failed-${Date.now()}-${++this.localMessageSequence}`,
          content,
          created_at: moment().unix(),
          role: 'user',
          type: 'message',
          failed: true,
          imageFile,
          attachment: nextAttachment,
          reusePreviousAttachment,
          errorMessage: error?.message || 'Message not sent.'
        }
        const index = this.failedMessages.findIndex(message => message.id === failedMessage.id)
        if (index === -1) this.failedMessages.push(failedMessage)
        else this.$set(this.failedMessages, index, failedMessage)
        this.scrollToBottom()
      } finally {
        if (version === this.conversationVersion) this.loadingSend = false
      }
    },

    async sendFeedback() {
      if (this.loadingFeedback) return

      const content = this.supportText.trim()
      if (!content && !this.isIssueFeedback) {
        this.supportValidation = 'Please write what happened.'
        this.$refs.supportInput?.focus()
        return
      }

      this.supportValidation = ''
      if (this.selectedFeedbackAttachmentIds.length && !this.feedbackAttachmentConsent) {
        this.feedbackAttachmentValidation = 'Confirm that the selected screenshots can be shared.'
        return
      }
      this.feedbackStatus = ''
      this.loadingFeedback = true
      const version = this.conversationVersion
      try {
        const attachments = await Promise.all(
          this.selectedFeedbackAttachmentIds.map(async fileId => {
            const url = await this.getSupportAttachmentUrl({ fileId, conversationId: this.conversationId })
            const response = await fetch(url)
            if (!response.ok) throw new Error('The selected screenshot could not be attached.')
            return {
              filename: `support-screenshot-${fileId}.png`,
              contentType: response.headers.get('content-type') || 'image/png',
              data: new Uint8Array(await response.arrayBuffer())
            }
          })
        )
        const payload = {
          content: `${content || 'Support issue submitted from chat.'}\nContext:\n${[...this.messages]
            .sort((a, b) => a.created_at - b.created_at)
            .map(item => `${item.role}: ${item.content}`)
            .join('\n')}${this.eventDescription ? `\nDiagnostic context:\n${this.eventDescription}` : ''}`,
          attachments
        }

        const sent = await this.postFeedback(payload)
        if (!sent || version !== this.conversationVersion) return

        this.supportText = ''
        this.visibleFeedbackForm = false
        this.feedbackReason = ''
        this.resetFeedbackAttachments()
        this.systemMessages.push({
          id: `support-status-${Date.now()}-${++this.localMessageSequence}`,
          role: 'system',
          type: 'status',
          title: 'Sent to ePeople Support',
          content: 'Your issue and safe error summary were attached.',
          created_at: moment().unix(),
          testId: 'support-request-success'
        })
        this.scrollToBottom(true)
      } catch (error) {
        if (version === this.conversationVersion) this.feedbackStatus = 'error'
      } finally {
        if (version === this.conversationVersion) this.loadingFeedback = false
      }
    },

    async init() {
      const version = this.conversationVersion
      this.loading = true
      try {
        await this.getMessages()
        this.scrollToBottom(true)
        this.$nextTick(() => this.$refs.messageInput?.focus())
      } catch (error) {
        if (version !== this.conversationVersion) return
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (version === this.conversationVersion) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.support-header {
  display: flex;
  min-height: 76px;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-color-base);
  background: var(--bg-component);
}

.support-avatar {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color-base);
  background: var(--bg-component);
}

.support-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.support-identity {
  min-width: 0;
}

.support-identity strong,
.support-identity span {
  display: flex;
  align-items: center;
}

.support-identity strong {
  color: var(--text-color);
  font-size: 17px;
}

.support-identity span {
  gap: 6px;
  color: var(--text-color-alpha8);
  font-size: 13px;
}

.support-identity i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
}

.support-close {
  display: flex;
  width: 40px;
  height: 40px;
  margin-left: auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: var(--text-color);
  border: 0;
  border-radius: calc(var(--border-radius) / 2);
  background: transparent;
  cursor: pointer;
}

.support-close:hover,
.support-close:focus-visible {
  background: var(--bg-row-hover);
}

.messages {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 18px;
  padding: 20px 18px 24px;
  overflow-x: hidden;
  overflow-y: auto;
  background: var(--bg-component);
}

.support-typing {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  color: var(--text-color-alpha8);
}

.support-typing img {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  padding: 4px;
  border-radius: calc(var(--border-radius) / 2);
  border: 1px solid var(--border-color-base);
  background: var(--bg-component);
}

.support-typing span {
  padding: 9px 11px;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius) var(--border-radius) var(--border-radius) 4px;
  background: var(--bg-component);
}

.new-reply-button {
  position: fixed;
  right: 28px;
  bottom: 96px;
  z-index: 2;
  padding: 8px 12px;
  border: 0;
  border-radius: var(--border-radius);
  background: var(--primary-color);
  color: #fff;
  cursor: pointer;
}

.footer {
  flex-shrink: 0;
  width: 100%;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--border-color-base);
  background: var(--bg-component);
}

.support-composer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.support-composer.dragging {
  outline: 2px solid var(--primary-color);
  outline-offset: 4px;
}

.composer-box {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 8px;
  padding: 7px;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius);
  background: var(--bg-body);
}

.composer-box:focus-within {
  border-color: var(--primary-color);
}

.chat-send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: calc(var(--border-radius) / 2);
  background: var(--primary-color);
  color: #fff;
}

.chat-send:disabled {
  opacity: 0.5;
  cursor: default;
}

.composer-hint {
  margin: 0 4px;
  color: var(--text-color-alpha8);
  font-size: 12px;
}

.image-policy-confirmation {
  padding: 12px;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius);
  background: var(--bg-row-hover);
}

.image-policy-confirmation p {
  margin: 4px 0 0;
  color: var(--text-color-alpha8);
}

.confirmation-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.support-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.support-validation {
  margin: 0;
  color: var(--text-color);
}

.footer ::v-deep .chat-input:focus,
.ant-select-focused .ant-select-selection,
.ant-cascader-picker-focus,
.ant-input-number:focus,
.ant-input-number-focused,
.ant-date-picker-focused {
  border-color: transparent;
  border: none;
}

.footer ::v-deep .chat-input {
  flex: 1;
  max-height: 56px !important;
  overflow-y: auto !important;
  background: transparent !important;
  border: none !important;
  padding: 8px;
  color: var(--text-color);
  box-shadow: none !important;
}

.footer ::v-deep textarea::placeholder {
  color: var(--text-color-alpha8);
  opacity: 1;
}

::v-deep .ant-drawer-body {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  background: var(--bg-body);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

button:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

@media (max-width: 520px) {
  .support-header,
  .messages {
    padding-right: 12px;
    padding-left: 12px;
  }

  .footer {
    padding-right: 12px;
    padding-left: 12px;
  }
}
</style>
